import * as proto from 'src/proto/compiled-protos';
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { LoadableContentComponent } from 'src/app/general/components/loadable-content/loadable-content.component';
import { BackendService } from '../../services/backend.service';
import { Formatter } from '../../util/formatter';
import { DataTableColumn, PreformattedTextColumn, StringColumn } from 'src/app/general/components/data-table/data-table-column';
import { ComponentUtil } from 'src/app/general/util/component-util';
import { SessionService } from '../../services/session.service';
import { DataTableComponent } from 'src/app/general/components/data-table/data-table.component';
import { LoadingController } from 'src/app/general/components/loadable-content/loading-controler';
import { ErrorResult, LoadingMessage } from 'src/app/general/util/result';
import { IndeterminatePaginatorComponent } from 'src/app/general/components/indeterminate-paginator/indeterminate-paginator.component';
import { IndeterminatePaginatorData, IndeterminatePaginatorModel } from 'src/app/general/components/indeterminate-paginator/indeterminate-paginator-model';
import { map, Observable, of } from 'rxjs';
import { RemoteLogToolbarModel } from '../../model/remote-log-toolbar-model';

// ----------------------------------------------------------------------------
// Usage Example

// Template:

// <app-device-remote-logs deviceId="{{device.id}}"></app-device-remote-logs>
// ----------------------------------------------------------------------------

@Component({
  selector: 'app-device-remote-logs',
  standalone: true,
  imports: [
    CommonModule,
    LoadableContentComponent,
    TranslateModule,
    MatCardModule,
    DataTableComponent,
    IndeterminatePaginatorComponent
  ],
  templateUrl: './device-remote-logs.component.html',
  styleUrls: ['./device-remote-logs.component.css']
})
export class DeviceRemoteLogsComponent implements IndeterminatePaginatorModel<proto.waiternow.common.IRemoteLogProto>, OnInit, OnChanges {
  @Input() deviceId!: string;

  remoteLogsLoadingController: LoadingController;

  remoteLogs: Array<proto.waiternow.common.IRemoteLogProto>;
  columns: Array<DataTableColumn<proto.waiternow.common.IRemoteLogProto>>;

  initialPaginatorData: IndeterminatePaginatorData<proto.waiternow.common.IRemoteLogProto>;
  paginatorModel: IndeterminatePaginatorModel<proto.waiternow.common.IRemoteLogProto>;

  remoteLogToolbarModel: RemoteLogToolbarModel;

  constructor(
      private backendService: BackendService,
      private sessionService: SessionService,
      private changeDetectorRef: ChangeDetectorRef) {
    this.remoteLogsLoadingController = new LoadingController();

    this.remoteLogs = new Array();
    this.columns = [
      new StringColumn(
        /* name= */ 'level',
        /* translateId= */ 'level',
        /* valueExtractor= */  log => Formatter.formatRemoteLoglevel(log.level)),
      new StringColumn(
        /* name= */ 'message',
        /* translateId= */ 'message',
        /* valueExtractor= */  log => log.message),
      new PreformattedTextColumn(
        /* name= */ 'stackTrace',
        /* translateId= */ 'stack_trace',
        /* valueExtractor= */  log => log.stackTrace,
        /* hideInCompactMode= */ true),
      ];

      this.initialPaginatorData = { data: new Array(), continuationToken: ''};
      this.paginatorModel = this;

      this.remoteLogToolbarModel = new RemoteLogToolbarModel(this.sessionService);
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (ComponentUtil.bindingChanged('deviceId', changes)) {
      this.ngOnInit();
    }
  }

  updatePage(page: proto.waiternow.common.IRemoteLogProto[]): void {
    this.remoteLogs = page;
    // This is needed because the update may be done async after the result of a backend call, and thus after Angular change detection process.
    this.changeDetectorRef.detectChanges();
  }

  fetchData(continuationToken: string | null | undefined): Observable<IndeterminatePaginatorData<proto.waiternow.common.IRemoteLogProto>> {
    if (!this.deviceId) {
      return of({data: [], continuationToken: ''});
    }

    return this.backendService.findDeviceRemoteLogsReturnObservable(this.deviceId, continuationToken)
      .pipe(
        map(remoteLogs  => {
          const paginatorData: IndeterminatePaginatorData<proto.waiternow.common.IRemoteLogProto> = {data: [], continuationToken: continuationToken};
          if (remoteLogs) {
            paginatorData.data = remoteLogs.logs;
            paginatorData.continuationToken = remoteLogs.continuationToken;
          }
          return paginatorData;
        })
      );
  }

  public loadInitialData(): void {
    this.remoteLogsLoadingController.onLoadingStarted(LoadingMessage.withMessageTranslateId('loading_remote_logs'));
    this.fetchData(/* continuationToken= */ null).subscribe(
      {
        next: paginatorData => {
          this.initialPaginatorData = paginatorData;
          this.remoteLogsLoadingController.onSuccess();
          // This is needed because the update may be done async after the result of a backend call, and thus after Angular change detection process.
          this.changeDetectorRef.detectChanges();
        },
        error: error => {
          this.remoteLogsLoadingController.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_internal'));
        }
      }
    );
  }
}
